import React, { useRef, useState } from 'react';
import emailjs from '@emailjs/browser';
import { useTranslation } from 'react-i18next';

// 공개 식별자. CRA는 REACT_APP_* 를 번들에 인라인함.
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_yswjds3';
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'template_1rlhbci';
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'DqUYUf0W7h-Q0SUic';

const ContactSection: React.FC = () => {
    const form = useRef<HTMLFormElement>(null);
    const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
    const { t } = useTranslation();

    const sendEmail = (e: React.FormEvent) => {
        e.preventDefault();
        setStatus('sending');

        if (form.current) {
            if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
                console.error('EmailJS 환경변수가 설정되지 않았습니다. .env.example 참고');
                setStatus('error');
                return;
            }
            emailjs
                .sendForm(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, form.current, EMAILJS_PUBLIC_KEY)
                .then(
                    (result: any) => {
                        setStatus('success');
                        form.current?.reset();
                    },
                    (error: any) => {
                        console.error('EmailJS send failed', error?.status);
                        setStatus('error');
                    }
                );
        }
    };

    return (
        <section id="contact" className="py-10 md:py-16 bg-gray-900 text-white">
            <div className="container mx-auto px-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-16">

                    {/* Contact Info */}
                    <div>
                        <h4 className="text-2xl md:text-base text-blue-500 font-bold uppercase tracking-widest mb-4 md:mb-6">
                            {t('contact.label')}
                        </h4>
                        <h2 className="text-2xl md:text-4xl font-extrabold mb-4 md:mb-8 leading-[1.2] whitespace-pre-line">
                            {t('contact.title')}
                        </h2>
                        <p className="text-gray-400 mb-8 md:mb-12 text-[16px] md:text-lg leading-relaxed whitespace-pre-line">
                            {t('contact.desc')}
                        </p>

                        <div className="space-y-6">
                            <div className="flex items-start">
                                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center text-blue-500 mr-4">📍</div>
                                <div>
                                    <p className="text-white font-bold mb-1 text-xl md:text-xl">{t('contact.address_label')}</p>
                                    <p className="text-gray-300 text-[17px] md:text-base">{t('contact.address')}</p>
                                </div>
                            </div>
                            <div className="flex items-start">
                                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center text-blue-500 mr-4">📞</div>
                                <div>
                                    <p className="text-white font-bold mb-1 text-xl md:text-xl">{t('contact.phone_label')}</p>
                                    <p className="text-gray-300 text-[17px] md:text-base">070-8080-3694</p>
                                </div>
                            </div>
                            <div className="flex items-start">
                                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center text-blue-500 mr-4">✉️</div>
                                <div>
                                    <p className="text-white font-bold mb-1 text-xl md:text-xl">{t('contact.email_label')}</p>
                                    <p className="text-gray-300 text-[17px] md:text-base">support@neubreze.com</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Form */}
                    <div className="bg-white rounded-2xl p-5 md:p-8 text-gray-900">
                        <h3 className="text-lg md:text-2xl font-bold mb-4 md:mb-6">{t('contact.form_title')}</h3>
                        <form ref={form} onSubmit={sendEmail} className="space-y-3 md:space-y-4">
                            <div>
                                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">{t('contact.name')}</label>
                                <input type="text" name="user_name" required className="w-full px-3 py-1.5 md:px-4 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition text-sm md:text-base" placeholder={t('contact.name_placeholder')} />
                            </div>
                            <div>
                                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">{t('contact.email')}</label>
                                <input type="email" name="user_email" required className="w-full px-3 py-1.5 md:px-4 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition text-sm md:text-base" placeholder={t('contact.email_placeholder')} />
                            </div>
                            <div>
                                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">{t('contact.phone')}</label>
                                <input type="tel" name="user_phone" className="w-full px-3 py-1.5 md:px-4 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition text-sm md:text-base" placeholder={t('contact.phone_placeholder')} />
                            </div>
                            <div>
                                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">{t('contact.message')}</label>
                                <textarea name="message" required className="w-full px-3 py-1.5 md:px-4 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition h-24 md:h-32 text-sm md:text-base" placeholder={t('contact.message_placeholder')}></textarea>
                            </div>
                            <button type="submit" disabled={status === 'sending'} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 md:py-3 rounded-lg transition duration-300 disabled:bg-gray-400 text-sm md:text-base">
                                {status === 'sending' ? t('contact.sending') : t('contact.send')}
                            </button>
                            {status === 'success' && <p className="text-green-600 text-center text-xs md:text-sm mt-2">{t('contact.success')}</p>}
                            {status === 'error' && <p className="text-red-600 text-center text-xs md:text-sm mt-2">{t('contact.error')}</p>}
                        </form>
                    </div>
                </div>

                {/* Map Section */}
                <div className="mt-20">
                    <h3 className="text-lg md:text-2xl font-bold mb-8 border-l-4 border-blue-500 pl-4">{t('contact.map_title')}</h3>
                    <div className="w-full h-[150px] md:h-[400px] bg-gray-200 rounded-2xl overflow-hidden shadow-lg">
                        <iframe
                            title="Company Location"
                            width="100%"
                            height="100%"
                            frameBorder="0"
                            style={{ border: 0 }}
                            src={`https://maps.google.com/maps?q=${encodeURIComponent('시흥창업센터')}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                            allowFullScreen
                        ></iframe>
                    </div>
                    <p className="mt-4 text-gray-500 text-sm">{t('contact.map_address')}</p>
                </div>
            </div>
        </section>
    );
};

export default ContactSection;
